import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { useEffect, useState } from "react";
import api from "../../api/axios";
import { Button, Card, Col, Input, message, Modal, Row, Spin } from "antd";
import { getAllPackagesByResellerId } from "../../redux/slices/packageSlice";

interface Subscriber {
    subscriberId: number;
    imsi: string;
    iccid: string;
  }
  
  export default function AssignSimPackages() {
    const dispatch = useDispatch<AppDispatch>();
    const { packages, loading: packagesLoading } = useSelector((state: RootState) => state.packages);
    const { user } = useSelector((state: RootState) => state.auth);
  
    const [stock, setStock] = useState<Subscriber[]>([]);
    const [sales, setSales] = useState<Subscriber[]>([]);
    const [selectedSim, setSelectedSim] = useState<Subscriber | null>(null);
    const [toppingUpSimId, setToppingUpSimId] = useState<number | null>(null);
    const [activeTab, setActiveTab] = useState<'stock' | 'sales'>('stock');
    const [search, setSearch] = useState('');
    const [packageSearch, setPackageSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [assigning, setAssigning] = useState(false);
  
    const fetchSims = async () => {
      setLoading(true);
      try {
        const response = await api.get('/pos/Resellers/getStockSalesSims');
        const stock: Subscriber[] = [];
        const sales: Subscriber[] = [];
  
        response.data.stockSims?.forEach((item) => {
          item.imsiList?.forEach((x: any) => {
            stock.push({ subscriberId: item.subscriberId, imsi: x?.imsi, iccid: x?.iccid });
          });
        });
  
        response.data.salesSims?.forEach((item) => {
          item.imsiList?.forEach((x: any) => {
            sales.push({ subscriberId: item.subscriberId, imsi: x?.imsi, iccid: x?.iccid });
          });
        });
  
        setStock(stock);
        setSales(sales);
      } catch {
        message.error('Failed to load SIMs');
      } finally {
        setLoading(false);
      }
    };
  
    useEffect(() => {
      fetchSims();
      dispatch(getAllPackagesByResellerId());
    }, [dispatch]);
  
    const sims = activeTab === 'stock' ? stock : sales;
    const filteredSims = sims.filter(sim =>
      [sim.imsi, sim.iccid, sim.subscriberId.toString()].some(val =>
        val?.toLowerCase().includes(search.toLowerCase())
      )
    );
    const filteredPackages = packages.filter(pkg =>
      pkg.prepaidPackageTemplateName?.toLowerCase().includes(packageSearch.toLowerCase())
    );
  
    const handleAssign = async (packageId: string, sim: Subscriber) => {
      const selectedPackage = packages.find(pkg => pkg.prepaidPackageTemplateId === packageId);
      if (!selectedPackage) return;
  
      Modal.confirm({
        title: 'Confirm Package Assignment',
        content: `Are you sure you want to assign "${selectedPackage.prepaidPackageTemplateName}" to ICCID ${sim.iccid}?`,
        okText: 'Yes',
        cancelText: 'No',
        onOk: async () => {
          setAssigning(true);
          try {
            await api.post('pos/Resellers/sim/assignPackage', {
              iccid: sim.iccid,
              packageId: packageId,
              cost: selectedPackage.cost
            });
            message.success('Package assigned successfully');
            setSelectedSim(null);
            setToppingUpSimId(null);
            fetchSims();
          } catch (error: any) {
            message.error(error.displayMessage || 'Failed to assign package');
          } finally {
            setAssigning(false);
          }
        },
      });
    };
  
    const handleMoveToStock = async (sim: Subscriber) => {
        Modal.confirm({
          title: 'Confirm Move to Stock',
          content: `Are you sure you want to move ICCID ${sim.iccid} to stock account?`,
          okText: 'Yes',
          cancelText: 'No',
          onOk: async () => {
            try {
              await api.get(`/pos/Resellers/moveIMSItoStockAccount?iccid=${sim.iccid}`);
              message.success('SIM moved back to stock');
              fetchSims();
            } catch (error: any) {
              message.error(error.displayMessage || 'Failed to move SIM');
            }
          }
        });
      };
  
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 12, background: '#f0f2f5', padding: 8, borderRadius: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            <div
              onClick={() => { setActiveTab('stock'); setSelectedSim(null); setToppingUpSimId(null); }}
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                borderRadius: 6,
                backgroundColor: activeTab === 'stock' ? '#1890ff' : 'transparent',
                color: activeTab === 'stock' ? '#fff' : '#000',
                fontWeight: 500
              }}
            >
              Packages
            </div>
            <div
              onClick={() => { setActiveTab('sales'); setSelectedSim(null); setToppingUpSimId(null); }}
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                borderRadius: 6,
                backgroundColor: activeTab === 'sales' ? '#1890ff' : 'transparent',
                color: activeTab === 'sales' ? '#fff' : '#000',
                fontWeight: 500
              }}
            >
              Top up
            </div>
          </div>
        </div>
  
        <Row gutter={[16, 16]}>
          {/* SIM List */}
          <Col xs={24} md={8} style={{ height: 'calc(100vh - 200px)', minHeight: '600px', overflowY: 'auto' }}>
            <Input
              placeholder="Search IMSI or ICCID"
              value={search}
              onChange={(e) => {
                const value = e.target.value;
                setSearch(value);
                setSelectedSim(null);
                setToppingUpSimId(null);
              }}
              style={{ marginBottom: 16 }}
            />
  
            {loading ? (
              <Spin />
            ) : (
              filteredSims.map(sim => {
                const isSelected = selectedSim?.subscriberId === sim.subscriberId;
                const isToppingUp = toppingUpSimId === sim.subscriberId;
                const isDisabled = toppingUpSimId !== null && !isToppingUp;
  
                return (
                  <Card
                    key={`${sim.subscriberId}-${sim.imsi}`}
                    style={{
                      marginBottom: 12,
                      border: isSelected ? '2px solid #1890ff' : '',
                      opacity: isDisabled ? 0.5 : 1,
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                    }}
                    onClick={() => {
                      if (activeTab === 'stock') {
                        if (isDisabled) return;
                        setSelectedSim(isSelected ? null : sim);
                      }
                    }}
                    actions={
                      activeTab === 'sales'
                        ? isToppingUp
                          ? [<Button onClick={() => { setToppingUpSimId(null); setSelectedSim(null); }}>Cancel</Button>]
                          : [
                              <Button onClick={() => { setToppingUpSimId(sim.subscriberId); setSelectedSim(sim); }} type="primary">Top Up</Button>,
                              <Button danger onClick={() => handleMoveToStock(sim)}>Move</Button>
                            ]
                        : []
                    }
                  >
                    <div><strong>IMSI:</strong> {sim.imsi}</div>
                    <div><strong>ICCID:</strong> {sim.iccid}</div>
                    <div><strong>SubscriberId:</strong> {sim.subscriberId}</div>
                  </Card>
                );
              })
            )}
          </Col>
  
          {/* Packages */}
          <Col xs={24} md={16}>
            <h2>Available Packages</h2>
            <Input
              placeholder="Search packages..."
              value={packageSearch}
              onChange={(e) => setPackageSearch(e.target.value)}
              style={{ marginBottom: 16 }}
            />
            {packagesLoading ? (
              <Spin />
            ) : (
              <Row gutter={[16, 16]}>
                {filteredPackages.map(pkg => (
                  <Col key={pkg.id} xs={24} sm={12} lg={8}>
                    <Card
                      title={pkg.prepaidPackageTemplateName}
                      bordered
                      actions={[
                        <Button
                          type="primary"
                          loading={assigning}
                          disabled={!selectedSim || (activeTab === 'sales' && toppingUpSimId !== selectedSim.subscriberId)}
                          onClick={() => handleAssign(pkg.prepaidPackageTemplateId, selectedSim!)}
                        >
                          Assign
                        </Button>
                      ]}
                    >
                      {user?.showPrices && (
                        <p>{(pkg.dataByte / (1024 * 1024)).toFixed(0)} MB</p>
                      )}
                      <p>{pkg.periodDays} Days</p>
                      {user?.showPrices && (
                        <p>{pkg.cost}€</p>
                      )}
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Col>
        </Row>
      </>
    );
  }